import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as ApplicationAccountsService from '../services/ApplicationAccountsService';
import { ApplicationAccount } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Page size is a SERVER concern under token pagination — fixed by the module and
// sent as `limit`, never chosen by the user through a rows-per-page dropdown.
const DEFAULT_LIMIT = 10;

// Query keys
export const applicationAccountsKeys = {
    all: ['applicationAccounts'] as const,
    lists: () => [...applicationAccountsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...applicationAccountsKeys.lists(), { filters }] as const,
    details: () => [...applicationAccountsKeys.all, 'detail'] as const,
    detail: (id: string) => [...applicationAccountsKeys.details(), id] as const,
};

// Hooks
export const useApplicationAccountsQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery({
        // `filters` carries applicationId and every other filter/search param the
        // server sees. Omitting one here silently serves the previous filter's cache.
        queryKey: applicationAccountsKeys.list(filters),
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            ApplicationAccountsService.fetchRecords({
                filter: filters,
                limit: DEFAULT_LIMIT,
                nextToken: pageParam,
            }),
        // MUST return undefined — not null, and never the same token back — or
        // React Query refetches forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
    });
};

export const useApplicationAccountQuery = (id: string) => {
    return useQuery({
        queryKey: applicationAccountsKeys.detail(id),
        queryFn: () => ApplicationAccountsService.fetch(id),
        enabled: !!id,
    });
};

export const useCreateApplicationAccountMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationAccountsService.add,
        onSuccess: () => {
            notifySuccess('Application account created');
            queryClient.invalidateQueries({ queryKey: applicationAccountsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateApplicationAccountMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: ApplicationAccount }) =>
            ApplicationAccountsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Application account updated');
            queryClient.invalidateQueries({ queryKey: applicationAccountsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: applicationAccountsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeleteApplicationAccountMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationAccountsService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Application account deleted');
            queryClient.invalidateQueries({ queryKey: applicationAccountsKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useApplicationAccountsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
